/**
 * useDAGExecution Hook - DAG Workflow Execution Management
 *
 * FERP CRITICAL: Domain-agnostic DAG execution control
 * Life-critical workflow execution with emergency controls
 *
 * Principles:
 * - All data from backend GraphQL (no hardcoded values)
 * - Repository pattern (GraphQL queries/mutations only)
 * - Polling-based real-time updates (no WebSocket subscriptions yet)
 * - Backend ID generation (never generate IDs client-side)
 * - Immutable audit trails for all execution actions
 * - Life-critical emergency controls (pause/cancel)
 * - 16 primitive types (DAG is workflow execution primitive)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
type ApolloError = Error;
import { gql } from '@apollo/client';
import { useCurrentTenant } from '../../hooks/useCurrentTenant';
import { useAuth } from '../../hooks/useAuth';
import { notifications } from '@mantine/notifications';

// ============================================================
// GraphQL Queries
// ============================================================

const DAG_EXECUTION_STATUS_QUERY = gql`
  query GetDAGExecutionStatus($executionId: ID!) {
    dag_execution_status(execution_id: $executionId) {
      execution_id
      status
      started_at
      completed_at
      total_nodes
      completed_nodes
      failed_nodes
      active_nodes
    }
  }
`;

const DAG_PERFORMANCE_METRICS_QUERY = gql`
  query GetDAGPerformanceMetrics($executionId: ID!) {
    dag_performance_metrics(execution_id: $executionId) {
      execution_id
      total_duration_ms
      bottleneck_nodes
      critical_path
      parallelism_factor
      node_metrics {
        node_id
        duration_ms
        status
        criticality
      }
    }
  }
`;

// ============================================================
// GraphQL Mutations
// ============================================================

const EXECUTE_DAG_WORKFLOW_MUTATION = gql`
  mutation ExecuteDAGWorkflow($input: ExecuteDAGWorkflowInput!) {
    execute_dag_workflow(input: $input) {
      success
      execution_id
      message
      result {
        execution_id
        status
        started_at
        nodes_executed
        nodes_failed
      }
    }
  }
`;

const PAUSE_DAG_EXECUTION_MUTATION = gql`
  mutation PauseDAGExecution($executionId: ID!) {
    pause_dag_execution(execution_id: $executionId) {
      success
      execution_id
      new_status
      message
      timestamp
    }
  }
`;

const RESUME_DAG_EXECUTION_MUTATION = gql`
  mutation ResumeDAGExecution($executionId: ID!) {
    resume_dag_execution(execution_id: $executionId) {
      success
      execution_id
      new_status
      message
      timestamp
    }
  }
`;

const CANCEL_DAG_EXECUTION_MUTATION = gql`
  mutation CancelDAGExecution($executionId: ID!) {
    cancel_dag_execution(execution_id: $executionId) {
      success
      execution_id
      new_status
      message
      timestamp
    }
  }
`;

const RETRY_FAILED_NODE_MUTATION = gql`
  mutation RetryFailedNode($input: RetryFailedNodeInput!) {
    retry_failed_node(input: $input) {
      success
      execution_id
      node_id
      new_status
      message
      timestamp
    }
  }
`;

const REPLAY_DAG_EXECUTION_MUTATION = gql`
  mutation ReplayDAGExecution($executionId: ID!) {
    replay_dag_execution(execution_id: $executionId) {
      success
      execution_id
      message
      result {
        execution_id
        status
        started_at
        nodes_executed
        nodes_failed
      }
    }
  }
`;

// ============================================================
// TypeScript Types (Backend-Driven)
// ============================================================

export enum DAGExecutionStatusEnum {
  Pending = 'Pending',
  Running = 'Running',
  Completed = 'Completed',
  Failed = 'Failed',
  Paused = 'Paused',
  Cancelled = 'Cancelled',
}

export enum DAGNodeStatusEnum {
  Pending = 'Pending',
  Running = 'Running',
  Completed = 'Completed',
  Failed = 'Failed',
  Skipped = 'Skipped',
  Blocked = 'Blocked',
}

export enum DAGNodeTypeEnum {
  Task = 'Task',
  Decision = 'Decision',
  Parallel = 'Parallel',
  Sequential = 'Sequential',
  Conditional = 'Conditional',
}

export enum DAGCriticalityEnum {
  Low = 'Low',
  Medium = 'Medium',
  High = 'High',
  Critical = 'Critical',
}

export interface DAGExecutionStatus {
  execution_id: string;
  status: DAGExecutionStatusEnum;
  started_at?: string;
  completed_at?: string;
  total_nodes: number;
  completed_nodes: number;
  failed_nodes: number;
  active_nodes: string[];
}

export interface DAGNodeMetrics {
  node_id: string;
  duration_ms: number;
  status: DAGNodeStatusEnum;
  criticality: DAGCriticalityEnum;
}

export interface DAGPerformanceMetrics {
  execution_id: string;
  total_duration_ms: number;
  bottleneck_nodes: string[];
  critical_path: string[];
  parallelism_factor: number;
  node_metrics: DAGNodeMetrics[];
}

export interface DAGNode {
  id: string;
  node_type: DAGNodeTypeEnum;
  label: string;
  criticality: DAGCriticalityEnum;
  timeout_ms?: number;
  metadata?: Record<string, string>;
}

export interface DAGEdge {
  source: string;
  target: string;
  condition?: string;
}

export interface DAGWorkflowInput {
  name: string;
  version: string;
  nodes: DAGNode[];
  edges: DAGEdge[];
  metadata?: Record<string, string>;
}

export interface ExecuteDAGWorkflowInput {
  workflow: DAGWorkflowInput;
  created_by: string;
}

export interface RetryFailedNodeInput {
  execution_id: string;
  node_id: string;
}

// ============================================================
// Hook Implementation
// ============================================================

export interface UseDAGExecutionProps {
  executionId?: string;
  pollingInterval?: number; // milliseconds (default: 2000)
  autoRefresh?: boolean; // Enable automatic polling
}

export interface UseDAGExecutionReturn {
  // State
  executionStatus: DAGExecutionStatus | null;
  performanceMetrics: DAGPerformanceMetrics | null;
  isLoading: boolean;
  error: ApolloError | null;

  // Mutations
  executeWorkflow: (input: ExecuteDAGWorkflowInput) => Promise<string | null>; // Returns execution_id
  pauseExecution: (executionId: string) => Promise<boolean>;
  resumeExecution: (executionId: string) => Promise<boolean>;
  cancelExecution: (executionId: string) => Promise<boolean>;
  retryNode: (input: RetryFailedNodeInput) => Promise<boolean>;
  replayExecution: (executionId: string) => Promise<string | null>;

  // Utilities
  refreshStatus: () => Promise<void>;
  refreshMetrics: () => Promise<void>;
}

export function useDAGExecution(props?: UseDAGExecutionProps): UseDAGExecutionReturn {
  const { executionId, pollingInterval = 2000, autoRefresh = true } = props || {};
  const { tenantId } = useCurrentTenant();
  const { currentUser } = useAuth();

  // Local state
  const [executionStatus, setExecutionStatus] = useState<DAGExecutionStatus | null>(null);
  const [performanceMetrics, setPerformanceMetrics] = useState<DAGPerformanceMetrics | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Query: Execution Status (manual fetch, no Apollo polling to control interval)
  const {
    data: statusData,
    error: statusError,
    loading: statusLoading,
    refetch: refetchStatus
  } = useQuery<Record<string, any>>(DAG_EXECUTION_STATUS_QUERY, {
    variables: { executionId },
    skip: !executionId,
    notifyOnNetworkStatusChange: true,
  });

  // Query: Performance Metrics
  const {
    data: metricsData,
    error: metricsError,
    loading: metricsLoading,
    refetch: refetchMetrics
  } = useQuery<Record<string, any>>(DAG_PERFORMANCE_METRICS_QUERY, {
    variables: { executionId },
    skip: !executionId,
  });

  // Mutations
  const [executeWorkflowMutation] = useMutation<Record<string, any>>(EXECUTE_DAG_WORKFLOW_MUTATION);
  const [pauseExecutionMutation] = useMutation<Record<string, any>>(PAUSE_DAG_EXECUTION_MUTATION);
  const [resumeExecutionMutation] = useMutation<Record<string, any>>(RESUME_DAG_EXECUTION_MUTATION);
  const [cancelExecutionMutation] = useMutation<Record<string, any>>(CANCEL_DAG_EXECUTION_MUTATION);
  const [retryNodeMutation] = useMutation<Record<string, any>>(RETRY_FAILED_NODE_MUTATION);
  const [replayExecutionMutation] = useMutation<Record<string, any>>(REPLAY_DAG_EXECUTION_MUTATION);

  // Update state when query data changes
  useEffect(() => {
    if (statusData?.dag_execution_status) {
      setExecutionStatus(statusData.dag_execution_status);
    }
  }, [statusData]);

  useEffect(() => {
    if (metricsData?.dag_performance_metrics) {
      setPerformanceMetrics(metricsData.dag_performance_metrics);
    }
  }, [metricsData]);

  // Polling for real-time updates (fallback for missing WebSocket subscriptions)
  useEffect(() => {
    if (executionId && autoRefresh) {
      // Clear any existing polling
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }

      // Start new polling interval
      pollingRef.current = setInterval(() => {
        refetchStatus();
        // Only refresh metrics if execution is completed (expensive query)
        if (executionStatus?.status === DAGExecutionStatusEnum.Completed) {
          refetchMetrics();
        }
      }, pollingInterval);

      // Cleanup on unmount
      return () => {
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
        }
      };
    }

    return undefined;
  }, [executionId, autoRefresh, pollingInterval, executionStatus?.status, refetchStatus, refetchMetrics]);

  // Execute workflow
  const executeWorkflow = useCallback(async (input: ExecuteDAGWorkflowInput): Promise<string | null> => {
    try {
      const { data } = await executeWorkflowMutation({
        variables: { input },
      });

      if (data?.execute_dag_workflow?.success) {
        notifications.show({
          title: 'DAG Workflow Started',
          message: data?.execute_dag_workflow.message,
          color: 'green',
        });
        return data?.execute_dag_workflow.execution_id ?? null;
      } else {
        notifications.show({
          title: 'Workflow Execution Failed',
          message: data?.execute_dag_workflow?.message || 'Unknown error',
          color: 'red',
        });
        return null;
      }
    } catch (error) {
      notifications.show({
        title: 'Execution Error',
        message: error instanceof Error ? error.message : 'Failed to execute workflow',
        color: 'red',
      });
      return null;
    }
  }, [executeWorkflowMutation]);

  // Pause execution
  const pauseExecution = useCallback(async (execId: string): Promise<boolean> => {
    try {
      const { data } = await pauseExecutionMutation({
        variables: { executionId: execId },
      });

      if (data?.pause_dag_execution?.success) {
        notifications.show({
          title: 'Execution Paused',
          message: data.pause_dag_execution.message,
          color: 'blue',
        });
        await refetchStatus();
        return true;
      } else {
        notifications.show({
          title: 'Pause Failed',
          message: data?.pause_dag_execution?.message || 'Unknown error',
          color: 'red',
        });
        return false;
      }
    } catch (error) {
      notifications.show({
        title: 'Pause Error',
        message: error instanceof Error ? error.message : 'Failed to pause execution',
        color: 'red',
      });
      return false;
    }
  }, [pauseExecutionMutation, refetchStatus]);

  // Resume execution
  const resumeExecution = useCallback(async (execId: string): Promise<boolean> => {
    try {
      const { data } = await resumeExecutionMutation({
        variables: { executionId: execId },
      });

      if (data?.resume_dag_execution?.success) {
        notifications.show({
          title: 'Execution Resumed',
          message: data.resume_dag_execution.message,
          color: 'green',
        });
        await refetchStatus();
        return true;
      } else {
        notifications.show({
          title: 'Resume Failed',
          message: data?.resume_dag_execution?.message || 'Unknown error',
          color: 'red',
        });
        return false;
      }
    } catch (error) {
      notifications.show({
        title: 'Resume Error',
        message: error instanceof Error ? error.message : 'Failed to resume execution',
        color: 'red',
      });
      return false;
    }
  }, [resumeExecutionMutation, refetchStatus]);

  // Cancel execution
  const cancelExecution = useCallback(async (execId: string): Promise<boolean> => {
    try {
      const { data } = await cancelExecutionMutation({
        variables: { executionId: execId },
      });

      if (data?.cancel_dag_execution?.success) {
        notifications.show({
          title: 'Execution Cancelled',
          message: data?.cancel_dag_execution.message,
          color: 'orange',
        });
        await refetchStatus();
        return true;
      } else {
        notifications.show({
          title: 'Cancel Failed',
          message: data?.cancel_dag_execution?.message || 'Unknown error',
          color: 'red',
        });
        return false;
      }
    } catch (error) {
      notifications.show({
        title: 'Cancel Error',
        message: error instanceof Error ? error.message : 'Failed to cancel execution',
        color: 'red',
      });
      return false;
    }
  }, [cancelExecutionMutation, refetchStatus]);

  // Retry failed node
  const retryNode = useCallback(async (input: RetryFailedNodeInput): Promise<boolean> => {
    try {
      const { data } = await retryNodeMutation({
        variables: { input },
      });

      if (data?.retry_failed_node?.success) {
        notifications.show({
          title: 'Node Retry Started',
          message: data.retry_failed_node.message,
          color: 'blue',
        });
        await refetchStatus();
        return true;
      } else {
        notifications.show({
          title: 'Retry Failed',
          message: data?.retry_failed_node?.message || 'Unknown error',
          color: 'red',
        });
        return false;
      }
    } catch (error) {
      notifications.show({
        title: 'Retry Error',
        message: error instanceof Error ? error.message : 'Failed to retry node',
        color: 'red',
      });
      return false;
    }
  }, [retryNodeMutation, refetchStatus]);

  // Replay execution
  const replayExecution = useCallback(async (execId: string): Promise<string | null> => {
    try {
      const { data } = await replayExecutionMutation({
        variables: { executionId: execId },
      });

      if (data?.replay_dag_execution?.success) {
        notifications.show({
          title: 'Execution Replayed',
          message: data.replay_dag_execution.message,
          color: 'green',
        });
        return data?.replay_dag_execution.execution_id ?? null;
      } else {
        notifications.show({
          title: 'Replay Failed',
          message: data?.replay_dag_execution?.message || 'Unknown error',
          color: 'red',
        });
        return null;
      }
    } catch (error) {
      notifications.show({
        title: 'Replay Error',
        message: error instanceof Error ? error.message : 'Failed to replay execution',
        color: 'red',
      });
      return null;
    }
  }, [replayExecutionMutation]);

  // Utilities
  const refreshStatus = useCallback(async () => {
    await refetchStatus();
  }, [refetchStatus]);

  const refreshMetrics = useCallback(async () => {
    await refetchMetrics();
  }, [refetchMetrics]);

  return {
    // State
    executionStatus,
    performanceMetrics,
    isLoading: statusLoading || metricsLoading,
    error: statusError || metricsError || null,

    // Mutations
    executeWorkflow,
    pauseExecution,
    resumeExecution,
    cancelExecution,
    retryNode,
    replayExecution,

    // Utilities
    refreshStatus,
    refreshMetrics,
  };
}
